import { useEffect, useRef } from 'react';
import { isDoubleTap, SINGLE_TAP_DELAY_MS, seekDirectionForTap } from './touchSeek.ts';

/**
 * Mobil oynatıcı dokunma davranışı.
 *
 * - Tek dokunma: kontrolleri açar/kapatır (260 ms gecikmeyle).
 * - Çift dokunma: sol yarı -10 sn, sağ yarı +10 sn.
 * - Kontrol düğmesine dokunma: bekleyen tek dokunmayı iptal eder.
 *
 * Zamanlayıcı ve son dokunma durumu bu kancada tutulur; `WatchScreen` yalnız
 * geri çağrıları bağlar. Böylece oynatıcı bileşeninin dallanması büyümez.
 */
export function useTouchSeek(
  element: HTMLElement | null,
  {
    onSeek,
    onToggleControls,
    onActivity,
    onFeedback,
  }: {
    onSeek: (deltaMs: number) => void;
    onToggleControls: () => void;
    /** Düğme/menü dokunuşunda kontrolleri canlı tut. */
    onActivity: () => void;
    onFeedback: (deltaMs: number) => void;
  },
): void {
  const timerRef = useRef<number | null>(null);
  const lastTapAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (element === null) return;

    const clearPendingTap = (): void => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      lastTapAtRef.current = null;
    };

    const scheduleSingleTap = (): void => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        lastTapAtRef.current = null;
        onToggleControls();
      }, SINGLE_TAP_DELAY_MS);
    };

    const handleVideoTap = (pointer: PointerEvent): void => {
      if (pointer.pointerType !== 'touch' && pointer.pointerType !== 'pen') {
        onToggleControls();
        return;
      }

      const now = Date.now();
      if (isDoubleTap(lastTapAtRef.current, now)) {
        clearPendingTap();
        const rect = element.getBoundingClientRect();
        const delta =
          seekDirectionForTap(pointer.clientX, rect.left, rect.width) === 'forward'
            ? 10_000
            : -10_000;
        onSeek(delta);
        onFeedback(delta);
        return;
      }

      lastTapAtRef.current = now;
      scheduleSingleTap();
    };

    const handlePointerDown = (event: Event): void => {
      if (event.target instanceof HTMLVideoElement) {
        handleVideoTap(event as PointerEvent);
        return;
      }
      clearPendingTap();
      onActivity();
    };

    const handlePointerMove = (event: Event): void => {
      // Dokunmatik parmak kayması "aktivite" sayılmaz; aksi hâlde tek dokunma
      // kontrolleri gizler, hareket hemen geri açar.
      if ((event as PointerEvent).pointerType === 'touch') return;
      onActivity();
    };

    element.addEventListener('pointerdown', handlePointerDown);
    element.addEventListener('pointermove', handlePointerMove);
    return () => {
      element.removeEventListener('pointerdown', handlePointerDown);
      element.removeEventListener('pointermove', handlePointerMove);
      clearPendingTap();
    };
  }, [element, onSeek, onToggleControls, onActivity, onFeedback]);
}
